/**
 * EmailPreferencesPage - Email Preference Management
 * Feature 3: Email Intelligence Platform — Phase 2
 * MDM-EMAIL-P2-001 / EMAIL-020
 *
 * Allows authenticated users to manage their email preferences
 * from the Account Dashboard 'Email' tab.
 */
import React, { useState, useEffect } from 'react';
import { Loader2, Save, CheckCircle, AlertCircle } from 'lucide-react';
import { emailPreferencesAPI } from '../../services/api';

const CATEGORIES = [
  {
    key: 'marketing_enabled',
    label: 'Marketing Emails',
    description: 'Promotions, sales, and special offers',
  },
  {
    key: 'gap_alerts_enabled',
    label: 'Gap Alerts',
    description: 'Notifications when missing issues from your collection become available',
    frequencyKey: 'gap_alert_frequency',
    frequencyOptions: ['immediate', 'daily', 'weekly'],
  },
  {
    key: 'digest_enabled',
    label: 'Weekly Digest',
    description: 'Summary of new arrivals, price changes, and collection updates',
    frequencyKey: 'digest_frequency',
    frequencyOptions: ['daily', 'weekly', 'monthly'],
  },
  {
    key: 'convention_alerts_enabled',
    label: 'Convention Alerts',
    description: 'Upcoming conventions, exclusive releases, and event updates',
  },
  {
    key: 'recommendation_enabled',
    label: 'Recommendations',
    description: 'Personalized comic and collectible recommendations',
  },
  {
    key: 'transactional_enabled',
    label: 'Transactional',
    description: 'Order confirmations, shipping updates, password resets (always on)',
    alwaysOn: true,
  },
];

export default function EmailPreferencesPage({ user }) {
  const [preferences, setPreferences] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const data = await emailPreferencesAPI.getPreferences();
        if (!cancelled) setPreferences(data);
      } catch (err) {
        if (!cancelled) setError('Failed to load email preferences. Please try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess(null);
        setError(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  const handleToggle = (key) => {
    /* c8 ignore next */
    if (!preferences) return;
    setPreferences(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleFrequencyChange = (key, value) => {
    /* c8 ignore next */
    if (!preferences) return;
    setPreferences(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    /* c8 ignore next */
    if (!preferences) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const data = await emailPreferencesAPI.updatePreferences({
        marketing_enabled: preferences.marketing_enabled,
        gap_alerts_enabled: preferences.gap_alerts_enabled,
        digest_enabled: preferences.digest_enabled,
        convention_alerts_enabled: preferences.convention_alerts_enabled,
        recommendation_enabled: preferences.recommendation_enabled,
        gap_alert_frequency: preferences.gap_alert_frequency,
        digest_frequency: preferences.digest_frequency,
      });
      setPreferences(data);
      setSuccess('Email preferences saved successfully.');
    } catch (err) {
      setError('Failed to save preferences. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-white">Email Preferences</h2>
        <p className="text-sm text-zinc-400 mt-1">Control which emails you receive from MAESTRO</p>
      </div>

      {success && (
        <div className="flex items-center gap-2 p-3 bg-green-500/10 border border-green-500/30 rounded-lg text-green-400 text-sm">
          <CheckCircle className="w-4 h-4 flex-shrink-0" />
          {success}
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {preferences && (
        <>
          <div className="space-y-3">
            {CATEGORIES.map((cat) => (
              <div key={cat.key} className="bg-zinc-900 border border-zinc-800 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1 mr-4">
                    <h3 className="text-sm font-medium text-white">{cat.label}</h3>
                    <p className="text-xs text-zinc-500 mt-0.5">{cat.description}</p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={cat.alwaysOn ? true : !!preferences[cat.key]}
                    disabled={cat.alwaysOn}
                    onClick={() => !cat.alwaysOn && handleToggle(cat.key)}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 focus:ring-offset-zinc-900 ${
                      cat.alwaysOn || preferences[cat.key]
                        ? 'bg-orange-500'
                        : 'bg-zinc-700'
                    } ${cat.alwaysOn ? 'opacity-60 cursor-not-allowed' : ''}`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ${
                        cat.alwaysOn || preferences[cat.key]
                          ? 'translate-x-5'
                          : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>

                {cat.frequencyKey && preferences[cat.key] && (
                  <div className="mt-3 pt-3 border-t border-zinc-800">
                    <label className="text-xs text-zinc-400 block mb-1">Frequency</label>
                    <select
                      value={preferences[cat.frequencyKey] || cat.frequencyOptions[0]}
                      onChange={(e) => handleFrequencyChange(cat.frequencyKey, e.target.value)}
                      className="bg-zinc-800 border border-zinc-700 text-white text-sm rounded px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-orange-500"
                    >
                      {cat.frequencyOptions.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt.charAt(0).toUpperCase() + opt.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="flex justify-end">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium px-6 py-2 rounded-lg transition-colors"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save Preferences
            </button>
          </div>
        </>
      )}
    </div>
  );
}
